import React, { RefCallback, useEffect, useMemo } from "react";
import { Typography } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { stopLoading } from "@/components/state/general/loadingSlice";
import CrudFieldController from "@/components/tables/fields/CrudFieldController";
import CrudSelectInput from "@/components/tables/fields/CrudSelectInput";
import { requiredSelectionRule } from "@/components/tables/fields/fieldRules";
import {
  ANALOG_FLAG_FIELD_NAME,
  CREATE_SED_FOR_COMPANY_FIELD_NAME,
  PORTAL_REGISTERED_FLAG_FIELD_NAME,
  SUBJECT_SET_FIELD_NAME,
} from "@/components/tables/fields/fieldNameRules";
import {
  buildSelectOptions,
  findSelectedOptions,
} from "@/components/tables/fields/selectOptions";
import {
  isDepartmentManagerOption,
  isSedDepartmentSelectionLocked,
} from "@/components/tables/fields/selectLockRules";
import { useSelectFieldItems } from "@/components/tables/fields/useSelectFieldItems";
import type { CrudModalField } from "@/components/tables/fields/crudModalField";
import styles from "@/components/tables/fields/crudSelectField.module.css";
import wrapperStyles from "@/components/tables/fields/crudField.module.css";

export interface CrudSelectFieldProps {
  field: Pick<
    CrudModalField,
    | "id"
    | "header"
    | "required"
    | "errorMessage"
    | "entity"
    | "multiselect"
    | "selectOptions"
    | "userEntityValueField"
    | "customLabelForGenericSelect"
    | "showAddressForBuildingSelect"
    | "autoFocus"
    | "disableColumn"
    | "useGlobalStateToImpersonate"
    | "additionalUrlParameters"
    | "getOnlyActiveUsers"
    | "additionalRoutePartsforGet"
  >;
  entityOfTable?: string;
  modalMode?: string;
  companySubjectId?: string;
  subjectIdForBuildings?: unknown;
  limit?: string;
  id?: string;
  ignoreGenericCrudSelectMaxWidth?: boolean;
  validationTargetRef?: RefCallback<HTMLElement>;
  onChange?: (value: any) => void;
  handleError?: (error: any) => void;
}

const LOCK_SOURCE_FIELD_NAMES = [
  PORTAL_REGISTERED_FLAG_FIELD_NAME,
  CREATE_SED_FOR_COMPANY_FIELD_NAME,
  SUBJECT_SET_FIELD_NAME,
];

const toSelectedValue = (selection: any, multiselect?: boolean) => {
  if (multiselect) {
    return Array.isArray(selection)
      ? selection.map((option: any) => option.value)
      : [];
  }
  return selection ? selection.value : "";
};

const CrudSelectField = ({
  field,
  entityOfTable,
  modalMode,
  companySubjectId,
  subjectIdForBuildings,
  limit,
  id,
  ignoreGenericCrudSelectMaxWidth,
  validationTargetRef,
  onChange,
  handleError,
}: CrudSelectFieldProps) => {
  const name = field.id as string;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { control, getValues } = useFormContext();
  useWatch({ control, name: LOCK_SOURCE_FIELD_NAMES });
  const [portalRegistered, createSedForCompany, subjectSet] = getValues(
    LOCK_SOURCE_FIELD_NAMES
  ); // useWatch misses values set before this field subscribed, e.g. the person list an earlier column resets on mount
  const { items, itemsLoaded } = useSelectFieldItems({
    field,
    companySubjectId,
    subjectIdForBuildings,
    limit,
    handleError,
  });
  const options = useMemo(
    () => buildSelectOptions(items, field, t),
    [items, field.entity, field.userEntityValueField, field.showAddressForBuildingSelect, t]
  );

  useEffect(() => {
    if (options.length !== 0 || itemsLoaded) dispatch(stopLoading());
  }, [options, itemsLoaded]);

  const analogLockedForRegisteredUser =
    entityOfTable === "user" &&
    modalMode === "edit" &&
    name === ANALOG_FLAG_FIELD_NAME &&
    portalRegistered === true;
  const locked =
    !itemsLoaded ||
    !!field.disableColumn ||
    analogLockedForRegisteredUser ||
    isSedDepartmentSelectionLocked({
      entityOfTable,
      entity: field.entity,
      createSedForCompany,
      subjectSet,
    });
  const label = field.customLabelForGenericSelect || field.header;
  const wrapperClassName = ignoreGenericCrudSelectMaxWidth
    ? styles.selectWrapper
    : `${styles.selectWrapper} ${styles.maxWidth}`;

  return (
    <CrudFieldController
      name={name}
      control={control}
      rules={requiredSelectionRule(field.required, field.errorMessage)}
      validationTargetRef={validationTargetRef}
      className={wrapperClassName}
      render={({ field: { onChange: setFormValue, onBlur, value }, error }) => (
        <>
          <label
            className={`${styles.dropdownWithRefLabel} ${
              analogLockedForRegisteredUser ? wrapperStyles.labelDisabled : ""
            }`}
          >
            {label}
            {field.required === true && (
              <span className={styles.labelRequired}>{"\u{002A}"}</span>
            )}
          </label>
          <CrudSelectInput
            name={name}
            id={id}
            options={options}
            value={findSelectedOptions(value, options)}
            isMulti={field.multiselect}
            loading={!itemsLoaded}
            disabled={locked}
            hasError={!!error}
            autoFocus={field.autoFocus}
            isOptionDisabled={(option) => isDepartmentManagerOption(option, t)}
            onChange={(selection) => {
              const selectedValue = toSelectedValue(selection, field.multiselect);
              onChange?.(selectedValue);
              setFormValue(selectedValue);
            }}
            onBlur={onBlur}
          />
          {analogLockedForRegisteredUser && (
            <Typography className={wrapperStyles.helperText}>
              {t("analog_cannot_set_for_registered_user")}
            </Typography>
          )}
        </>
      )}
    />
  );
};

export default CrudSelectField;
